import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  FlowerLotus, Calculator, ArrowLeft, Star, Sparkle,
  CheckCircle, XCircle, Crown, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import SEO from "../components/SEO";

/**
 * Pushkar Navamsa + Pushkar Bhaga calculator.
 *
 * Classical Vedic system: each rasi has two 3°20' "pushkar navamsa" arcs
 * (planets there gain benefic strength) and one exact "pushkar bhaga"
 * degree (planets within ±1° are extraordinarily potent). We show, for
 * each of the 9 D1 planets (Sun, Moon, Mars, Mercury, Jupiter, Venus,
 * Saturn, Rahu, Ketu), whether it lands in a PN arc, a PB point, both,
 * or neither — plus a compact reference legend.
 *
 * Zero LLM, pure Swiss-Ephemeris compute → free public tool.
 */
export default function PushkarPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", timezone: "Asia/Kolkata", place: "" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const compute = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time) {
      toast.error("Please enter your date and time of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pushkar/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='pushkar-result']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute — please recheck your birth details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="pushkar-page">
      <SEO
        title="Pushkar Navamsa + Pushkar Bhaga Calculator — Which Planets in Your Chart are Auspicious"
        description="Free Vedic astrology calculator — enter your birth details and see which of your 9 planets (Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn, Rahu, Ketu) sit inside a Pushkar Navamsa arc (auspicious) or land within ±1° of the exact Pushkar Bhaga degree (extraordinarily benefic). Swiss-Ephemeris precise, zero LLM."
        path="/tools/pushkar"
        keywords={[
          "pushkar navamsa calculator", "pushkar bhaga calculator",
          "pushkar navamsa in D1", "auspicious planets vedic astrology",
          "pushkar navamsa each rashi", "pushkar bhaga degrees",
        ]}
      />
      <Header />

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm text-muted mb-4"
          data-testid="pushkar-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <FlowerLotus size={14} weight="fill" /> Free · Pushkar Navamsa + Bhaga
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Which of your planets are in a <span className="italic text-gold">Pushkar zone</span>?
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light leading-relaxed">
          Classical Vedic astrology identifies two hyper-auspicious zones inside every rasi
          — <b className="text-[#3F2D1B]">Pushkar Navamsa</b> (two 3°20' arcs per sign where planets gain
          sustained benefic strength) and <b className="text-[#3F2D1B]">Pushkar Bhaga</b> (one exact
          degree per sign, ±1° orb, where the planet is nourished and protected). We check all
          9 D1 planets — Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn, Rahu, Ketu — against
          the canonical tables from <em>Jataka Parijata</em>.
        </p>

        {/* Form */}
        <form onSubmit={compute} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="pushkar-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="pushkar-time" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="pushkar-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="pushkar-place"
                  required={false}
                />
              </div>
            </label>
          </div>
          <button type="submit" disabled={busy}
            className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60"
            data-testid="pushkar-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Consulting the ephemeris..." : "Check my planets"}
          </button>
        </form>

        {result && <ResultBlock r={result} />}
      </main>

      <Footer />
    </div>
  );
}


function ResultBlock({ r }) {
  const c = r.counts || {};
  return (
    <div className="mt-10 space-y-5" data-testid="pushkar-result">
      {/* Hero summary */}
      <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
        <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
          <FlowerLotus size={14} weight="fill" /> Pushkar audit · your 9 planets
        </div>
        <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
          {c.pushkar_bhaga > 0 ? (
            <>{c.pushkar_bhaga} in <span className="italic text-amber-300">Pushkar Bhaga</span> · {c.pushkar_navamsa} in Pushkar Navamsa</>
          ) : c.pushkar_navamsa > 0 ? (
            <>{c.pushkar_navamsa} planet{c.pushkar_navamsa > 1 ? "s" : ""} in a <span className="italic text-amber-300">Pushkar Navamsa</span> arc</>
          ) : (
            <>No planets in Pushkar zones today</>
          )}
        </h2>
        <div className="mt-1 text-amber-200/80 text-sm">
          Lagna: {r?.lagna?.rashi || "—"} · Birth: {r?.birth?.place || ""}
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <StatPill label="Pushkar Bhaga" value={c.pushkar_bhaga || 0} accent />
          <StatPill label="Pushkar Navamsa" value={c.pushkar_navamsa || 0} />
          <StatPill label="Neither" value={c.neither ?? (c.total - (c.pushkar_navamsa || 0))} />
        </div>
      </div>

      {/* Planet table */}
      <div className="card-surface rounded-2xl overflow-hidden" data-testid="pushkar-table">
        <div className="px-5 sm:px-6 py-4 border-b border-soft flex items-center gap-2">
          <Star size={16} className="text-gold" weight="fill" />
          <div className="label-eyebrow text-gold">All 9 planets</div>
        </div>
        <div className="divide-y divide-soft">
          {r.planets.map((p) => (
            <PlanetRow key={p.name} p={p} />
          ))}
        </div>
      </div>

      {/* Reference legend */}
      <ReferenceLegend ref_={r.reference} />
    </div>
  );
}


function StatPill({ label, value, accent = false }) {
  return (
    <div className={`rounded-lg px-3 py-3 border ${accent ? "bg-amber-500/20 border-amber-400/50" : "bg-white/5 border-white/15"}`}>
      <div className="text-[10px] uppercase tracking-widest text-amber-200/80">{label}</div>
      <div className="mt-1 font-serif-display text-3xl leading-none">{value}</div>
    </div>
  );
}


function PlanetRow({ p }) {
  const isPB = p.in_pushkar_bhaga;
  const isPN = p.in_pushkar_navamsa;
  const tag = isPB ? "PB" : isPN ? "PN" : "—";
  const tagColour = isPB
    ? "bg-amber-100 text-amber-900 border-amber-300"
    : isPN
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : "bg-stone-100 text-stone-500 border-stone-200";
  return (
    <div
      className={`px-5 sm:px-6 py-4 flex items-start gap-4 flex-wrap ${
        isPB ? "bg-amber-50/50" : isPN ? "bg-emerald-50/40" : ""
      }`}
      data-testid={`pushkar-row-${p.name.toLowerCase()}`}
    >
      <div className="min-w-[110px] flex items-center gap-2">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
          isPB ? "bg-amber-500 text-white" : isPN ? "bg-emerald-500 text-white" : "bg-stone-200 text-stone-500"
        }`}>
          {isPB ? <Sparkle size={16} weight="fill" /> : isPN ? <CheckCircle size={16} weight="fill" /> : <XCircle size={14} />}
        </div>
        <div>
          <div className="font-medium text-[#3F2D1B]">{p.name}</div>
          <div className="text-[11px] text-muted">{p.retrograde ? "retrograde" : ""}</div>
        </div>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm text-[#3F2D1B]">
          <span className="font-serif-display text-lg">{p.rashi}</span>
          <span className="text-muted ml-2">{p.degree_str}</span>
          {p.house_from_lagna && <span className="text-muted ml-2">· House {p.house_from_lagna}</span>}
          {p.dignity && p.dignity !== "neutral" && (
            <span className={`ml-2 text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${
              p.dignity === "exalted" ? "bg-emerald-100 text-emerald-800" :
              p.dignity === "own" ? "bg-sky-100 text-sky-800" :
              p.dignity === "debilitated" ? "bg-rose-100 text-rose-800" :
              "bg-stone-100 text-stone-700"
            }`}>{p.dignity}</span>
          )}
        </div>
        <p className="text-xs text-muted mt-1">{p.verdict}</p>
        {isPN && p.pushkar_navamsa && (
          <div className="text-[11px] text-emerald-800 mt-1">
            Arc: {p.pushkar_navamsa.arc_start_str}–{p.pushkar_navamsa.arc_end_str}
            {" · "}Navamsa #{p.pushkar_navamsa.navamsa_number} of the sign
            {" · "}{p.pushkar_navamsa.tatva} element
          </div>
        )}
        {isPB && p.pushkar_bhaga && (
          <div className="text-[11px] text-amber-900 mt-1">
            Exact Pushkar Bhaga = {p.pushkar_bhaga.exact_degree_str}, orb {p.pushkar_bhaga.orb_str}
          </div>
        )}
      </div>
      <span className={`self-start text-[10px] font-bold tracking-widest px-2 py-1 rounded-full border ${tagColour}`}>
        {tag}
      </span>
    </div>
  );
}


function ReferenceLegend({ ref_ }) {
  if (!ref_) return null;
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="pushkar-legend">
      <div className="flex items-center gap-2 label-eyebrow text-gold">
        <Info size={14} weight="duotone" /> Canonical reference
      </div>

      <div className="mt-4">
        <div className="text-[11px] font-bold tracking-widest text-[#3F2D1B] mb-2">
          PUSHKAR NAVAMSA — by element (tatva) of the containing rasi
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(ref_.pushkar_navamsa_by_tatva).map(([tatva, arcs]) => (
            <div key={tatva} className="rounded-lg border border-soft bg-white/60 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Crown size={12} className="text-gold" weight="fill" />
                <div className="text-sm font-medium capitalize text-[#3F2D1B]">{tatva}</div>
                <div className="text-[11px] text-muted">
                  {(ref_.signs_by_tatva?.[tatva] || []).join(" · ")}
                </div>
              </div>
              <div className="text-[13px] text-muted">
                {arcs.map((a) => (
                  <div key={a.arc_number} className="font-mono">
                    Navamsa #{a.navamsa_number}: {a.start_str} – {a.end_str}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[11px] font-bold tracking-widest text-[#3F2D1B] mb-2">
          PUSHKAR BHAGA — exact degree per rasi (±{ref_.orb_degrees}° orb)
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-[13px]">
          {Object.entries(ref_.pushkar_bhaga_by_sign).map(([sign, deg]) => (
            <div key={sign} className="rounded-md border border-soft bg-white/60 px-3 py-1.5 flex items-center justify-between">
              <span className="text-[#3F2D1B]">{sign}</span>
              <span className="font-mono text-gold font-medium">{deg}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
